import { shrpaApi } from "./axios";
import dayjs from "dayjs";

// Types
import {
  ContentCalendarMatchedResults,
  ContentCalendarInput,
  ContentCalendarItem,
  ContentCalendarGuidanceData,
  MarkReadyForPostGenRequest,
  StatusChangeCheckRequest,
  ContentCalendarItemMatch,
  SocialContentUploadResult,
  SocialContentUploadPayload,
  GetCalendarInputsResponse,
  MatchAndGeneratePostRequest,
  MatchAndGeneratePostResponse,
  FullManualPostCreationRequest,
  FullManualPostCreationResponse,
  CalendarDashboardSummaryResult,
  CalendarStatusResult,
  CalendarGenResultSummaryData,
  NextFutureCalendarResult,
  CalendarInfo,
  ContentCalendarUserSettingsData,
} from "@contracts/contentCalendar";
import { PhotoGalleryCustomerData } from "@contracts/photosPageData";
import { getCalendarId } from "@logic/calendarManager";

// NOTE! Some of the calls in here are probably deprecated now that we always hide topics without posts.

export default {
  // Get calendar operation status
  async getOperationStatus(customerId: string): Promise<CalendarStatusResult> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-items/${customerId}/${calendarId}/operation-status`;
    const { data } = await shrpaApi.get<CalendarStatusResult>(uri);
    return data;
  },

  // ContentCalendarInput methods
  async getAllInputsForCustomer(customerId: string, autoCreateGeneral: boolean, autoCreateLocal: boolean): Promise<GetCalendarInputsResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}/v2?autoCreateGeneral=${autoCreateGeneral}&autoCreateLocal=${autoCreateLocal}`;
    const { data } = await shrpaApi.get<GetCalendarInputsResponse>(uri);
    return data;
  },

  async getInputById(customerId: string, id: string): Promise<ContentCalendarInput> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}/${id}`;
    const { data } = await shrpaApi.get<ContentCalendarInput>(uri);
    return data;
  },

  async createInput(customerId: string, input: Omit<ContentCalendarInput, "id">): Promise<ContentCalendarInput> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.post<ContentCalendarInput>(uri, JSON.stringify(input), config);
    return data;
  },

  async updateInput(customerId: string, id: string, input: ContentCalendarInput): Promise<ContentCalendarInput> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}/${id}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<ContentCalendarInput>(uri, JSON.stringify(input), config);
    return data;
  },

  async deleteInput(customerId: string, inputId: string): Promise<void> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}/${inputId}`;
    await shrpaApi.delete(uri);
  },

  // ContentCalendarItem methods
  async getAllItemsForCustomer(
    customerId: string,
    isLoadPosts = true,
    isSyncSocialAccounts = true,
    hideTopicsWithoutPosts = true,
    includeDeleted = false,
    includePast = false
  ): Promise<ContentCalendarMatchedResults> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-items/${customerId}/${calendarId}/grouped?loadPosts=${isLoadPosts}&syncSocialAccounts=${isSyncSocialAccounts}&hideTopicsWithoutPosts=${hideTopicsWithoutPosts}&includeDeleted=${includeDeleted}&includePast=${includePast}`;
    const { data } = await shrpaApi.get<ContentCalendarMatchedResults>(uri);
    return data;
  },

  async getItemById(customerId: string, id: string): Promise<ContentCalendarItem> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-items/${customerId}/${calendarId}/${id}`;
    const { data } = await shrpaApi.get<ContentCalendarItem>(uri);
    return data;
  },

  async matchAndGeneratePost(customerId: string, request: MatchAndGeneratePostRequest): Promise<MatchAndGeneratePostResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-gen/${customerId}/${calendarId}/generate-post`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.post<MatchAndGeneratePostResponse>(uri, JSON.stringify(request), config);
    return data;
  },

  async createManualPost(customerId: string, request: FullManualPostCreationRequest): Promise<FullManualPostCreationResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-gen/${customerId}/${calendarId}/manual-post`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.post<FullManualPostCreationResponse>(uri, JSON.stringify(request), config);
    return data;
  },

  async triggerWeekPostGenDebug(customerId: string, weekKey: string): Promise<string> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-gen/${customerId}/${calendarId}/post-gen-for-week?weekKey=${encodeURIComponent(weekKey)}`;
    const { data } = await shrpaApi.put<string>(uri);
    return data;
  },

  async updateItem(customerId: string, calendarItem: ContentCalendarItem): Promise<ContentCalendarItem> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-items/${customerId}/${calendarId}/${calendarItem.id}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<ContentCalendarItem>(uri, JSON.stringify(calendarItem), config);
    return data;
  },

  async deleteItem(customerId: string, itemId: string): Promise<void> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-items/${customerId}/${calendarId}/${itemId}`;
    await shrpaApi.delete(uri);
  },

  async saveWeekGuidance(customerId: string, weekKey: string, userGuidance: string | null): Promise<ContentCalendarGuidanceData> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-guidance/${customerId}/${calendarId}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<ContentCalendarGuidanceData>(uri, { weekKey, userGuidance }, config);
    return data;
  },

  async getUserSettings(customerId: string): Promise<ContentCalendarUserSettingsData> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-user-settings/${customerId}/${calendarId}`;
    const { data } = await shrpaApi.get<ContentCalendarUserSettingsData>(uri);
    return data;
  },

  async updateUserSettings(customerId: string, body: ContentCalendarUserSettingsData): Promise<ContentCalendarUserSettingsData> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-user-settings/${customerId}/${calendarId}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<ContentCalendarUserSettingsData>(uri, JSON.stringify(body), config);
    return data;
  },

  async socialUploadContent(customerId: string, uploadedForPostTitle: string, photoIds: Array<string>): Promise<SocialContentUploadResult> {
    const uri = `/social-content-upload/${customerId}`;

    const body: SocialContentUploadPayload = {
      uploadedForPostTitle,
      photoIds,
      clientSideDateTime: dayjs().format(),
    };

    const config = { headers: { "Content-Type": "application/json" } };

    const { data } = await shrpaApi.post<SocialContentUploadResult>(uri, JSON.stringify(body), config);

    return data;
  },

  async loadUploadGroups(customerId: string): Promise<Array<PhotoGalleryCustomerData>> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/social-content-upload/${customerId}/${calendarId}/upload-groups`;

    const { data } = await shrpaApi.get<Array<PhotoGalleryCustomerData>>(uri);

    return data;
  },

  async finishedUploading(customerId: string): Promise<void> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/social-content-upload/${customerId}/${calendarId}/finished-uploading`;
    const config = { headers: { "Content-Type": "application/json" } };
    await shrpaApi.put(uri, JSON.stringify({}), config);
  },

  async markCalendarItemsReadyForGen(request: MarkReadyForPostGenRequest) {
    const calendarId = await getCalendarId(request.customerId);
    const uri = `/content-calendar-items/${request.customerId}/${calendarId}/mark-ready-for-gen`;
    const config = { headers: { "Content-Type": "application/json" } };

    await shrpaApi.put(uri, JSON.stringify(request), config);
  },

  async checkCalendarItemsPostsGenerationStatus(customerId: string, request: StatusChangeCheckRequest): Promise<Array<ContentCalendarItemMatch>> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-items/${customerId}/${calendarId}/check-status`;
    const config = { headers: { "Content-Type": "application/json" } };

    const { data } = await shrpaApi.put<Array<ContentCalendarItemMatch>>(uri, JSON.stringify(request), config);

    return data;
  },

  // Regenerate Local Events
  async genLocalEvents(customerId: string, overwrite = true): Promise<Array<ContentCalendarInput>> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}/templates/local?overwrite=${overwrite}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<Array<ContentCalendarInput>>(uri, JSON.stringify({}), config);
    return data;
  },

  // Generate interests using AI
  async genInterests(customerId: string, save = false, overwrite = false): Promise<ContentCalendarUserSettingsData> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-inputs/${customerId}/${calendarId}/interests?save=${save}&overwrite=${overwrite}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<ContentCalendarUserSettingsData>(uri, JSON.stringify({}), config);
    return data;
  },

  // Dashboard summary
  async getDashboardSummary(customerId: string, maxPosts?: number): Promise<CalendarDashboardSummaryResult> {
    // NOTE! Using the default calendar here since we don't show the calendarId picker on the dashboard currently.
    let uri = `/content-calendar-dashboard/${customerId}`;
    if (maxPosts !== undefined) {
      uri += `?maxPosts=${maxPosts}`;
    }
    const { data } = await shrpaApi.get<CalendarDashboardSummaryResult>(uri);
    return data;
  },

  // Opt in to Atlas Social
  async optInToAtlas(customerId: string): Promise<void> {
    const uri = `/content-calendar-dashboard/${customerId}/opt-in`;
    await shrpaApi.put(uri, JSON.stringify({}));
  },

  // Test future calendar generation
  async testFutureCalendarGen(customerId: string, genStartDate: string, weeksToGen: number): Promise<CalendarGenResultSummaryData> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/future-calendar/${customerId}/${calendarId}/test?genStartDate=${genStartDate}&weeksToGen=${weeksToGen}`;
    const { data } = await shrpaApi.post<CalendarGenResultSummaryData>(uri);
    return data;
  },

  async getSummaries(customerId: string): Promise<CalendarGenResultSummaryData[]> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/future-calendar/${customerId}/${calendarId}/summaries`;
    const { data } = await shrpaApi.get<CalendarGenResultSummaryData[]>(uri);
    return data;
  },

  async getNextFutureCalendarSummary(customerId: string, calendarIdOverride?: string): Promise<NextFutureCalendarResult> {
    const calendarId = calendarIdOverride || (await getCalendarId(customerId));
    const uri = `/future-calendar/${customerId}/${calendarId}/next-summary`;
    const { data } = await shrpaApi.get<NextFutureCalendarResult>(uri);
    return data;
  },

  async markSummaryForRedo(customerId: string, summaryId: string): Promise<CalendarGenResultSummaryData> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/future-calendar/${customerId}/${calendarId}/${summaryId}/mark-for-redo`;
    const { data } = await shrpaApi.post<CalendarGenResultSummaryData>(uri);
    return data;
  },

  // Calendar management (multi-calendar support)
  async getCalendarsForCustomer(customerId: string, includeTest = false): Promise<CalendarInfo[]> {
    const uri = `/content-calendar/${customerId}/calendars?includeTest=${includeTest}`;
    const { data } = await shrpaApi.get<CalendarInfo[]>(uri);
    return data;
  },

  async createCalendar(customerId: string, calendarName: string, isTest = false): Promise<CalendarInfo> {
    const uri = `/content-calendar/${customerId}/calendars`;
    const { data } = await shrpaApi.post<CalendarInfo>(uri, { calendarName, isTest });
    return data;
  },

  async renameCalendar(customerId: string, calendarId: string, calendarName: string): Promise<CalendarInfo> {
    const uri = `/content-calendar/${customerId}/calendars/${calendarId}`;
    const { data } = await shrpaApi.put<CalendarInfo>(uri, { calendarName });
    return data;
  },

  async deleteCalendar(customerId: string, calendarId: string): Promise<void> {
    const uri = `/content-calendar/${customerId}/calendars/${calendarId}`;
    await shrpaApi.delete(uri);
  },

  async requestCalendar(customerId: string, calendarPurpose: string): Promise<void> {
    const uri = `/content-calendar-dashboard/${customerId}/request-calendar`;
    await shrpaApi.post(uri, { calendarPurpose });
  },
};
